import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, DollarSign } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Transfer {
  from: string;
  to: string;
  amount: number;
}

interface PaymentTransfersProps {
  gameId: string;
}

export const PaymentTransfers = ({ gameId }: PaymentTransfersProps) => {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    calculateTransfers();
  }, [gameId]);

  const calculateTransfers = async () => {
    try {
      // Get all payouts for this game
      const { data: payouts, error } = await supabase
        .from('payouts')
        .select(`
          *,
          players:player_id (name)
        `)
        .eq('game_id', gameId);

      if (error) throw error;

      if (!payouts || payouts.length === 0) {
        setTransfers([]);
        setLoading(false);
        return;
      }

      // Calculate simplified transfers using debt settling algorithm
      const balances: { [playerId: string]: { name: string; balance: number } } = {};
      
      payouts.forEach((payout: any) => {
        balances[payout.player_id] = {
          name: payout.players.name,
          balance: payout.net_amount
        };
      });

      // Separate debtors and creditors
      const debtors = Object.entries(balances).filter(([_, data]) => data.balance < 0);
      const creditors = Object.entries(balances).filter(([_, data]) => data.balance > 0);

      const calculatedTransfers: Transfer[] = [];

      // Calculate minimal transfers
      const debtorsCopy = debtors.map(([id, data]) => ({ id, name: data.name, amount: Math.abs(data.balance) }));
      const creditorsCopy = creditors.map(([id, data]) => ({ id, name: data.name, amount: data.balance }));

      while (debtorsCopy.length > 0 && creditorsCopy.length > 0) {
        const debtor = debtorsCopy[0];
        const creditor = creditorsCopy[0];

        const transferAmount = Math.min(debtor.amount, creditor.amount);

        calculatedTransfers.push({
          from: debtor.name,
          to: creditor.name,
          amount: transferAmount
        });

        debtor.amount -= transferAmount;
        creditor.amount -= transferAmount;

        if (debtor.amount === 0) {
          debtorsCopy.shift();
        }
        if (creditor.amount === 0) {
          creditorsCopy.shift();
        }
      }

      setTransfers(calculatedTransfers);
    } catch (error) {
      console.error('Error calculating transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (transfers.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Transfers Required</h3>
          <p className="text-muted-foreground">
            Game hasn't been settled yet or all players are even.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <DollarSign className="h-5 w-5 mr-2" />
            Simplified Payouts
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Minimum number of transactions required to settle all debts
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {transfers.map((transfer, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
            >
              <div className="flex items-center space-x-4">
                <div className="text-sm">
                  <span className="font-medium">{transfer.from}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <div className="text-sm">
                  <span className="font-medium">{transfer.to}</span>
                </div>
              </div>
              <div className="text-lg font-bold text-primary">
                ₹{transfer.amount.toFixed(2)}
              </div>
            </div>
          ))}
          
          <div className="mt-6 p-4 bg-primary/10 rounded-lg">
            <p className="text-sm text-center text-muted-foreground">
              Total transfers needed: <span className="font-medium">{transfers.length}</span>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};